import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { notifyCrmAlert } from "../lib/notifications";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import {
  Bell,
  Users,
  CheckCircle,
  TrendingUp,
  Clock,
  X,
  Check,
  FileText,
  UserPlus,
  ClipboardList,
} from "lucide-react";

const typeIcons = {
  lead_assigned: <Users size={14} className="text-blue-600" />,
  new_lead: <Users size={14} className="text-teal-600" />,
  lead_qualified: <CheckCircle size={14} className="text-emerald-600" />,
  deal_stage_changed: <TrendingUp size={14} className="text-amber-600" />,
  follow_up_reminder: <Clock size={14} className="text-orange-600" />,
  report_reviewed: <FileText size={14} className="text-violet-600" />,
  team_added: <UserPlus size={14} className="text-teal-600" />,
  task_assigned: <ClipboardList size={14} className="text-blue-600" />,
};

const typeBg = {
  lead_assigned: "bg-blue-50",
  new_lead: "bg-teal-50",
  lead_qualified: "bg-emerald-50",
  deal_stage_changed: "bg-amber-50",
  follow_up_reminder: "bg-orange-50",
  report_reviewed: "bg-violet-50",
  team_added: "bg-teal-50",
  task_assigned: "bg-blue-50",
};

export function NotificationBell() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const prevUnreadRef = useRef(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get("/notifications/unread-count");
      const newCount = res.data.count;
      // If count increased, trigger a browser push notification
      if (newCount > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        try {
          const latestRes = await api.get("/notifications");
          const latest = latestRes.data.find((n) => !n.is_read);
          if (latest) {
            notifyCrmAlert(latest.title, latest.message, latest.link || "/");
          }
        } catch {}
      }
      prevUnreadRef.current = newCount;
      setUnreadCount(newCount);
    } catch (err) {
      // silent
    }
  }, [api]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications");
      setNotifications(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Poll for unread count every 15 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error(err);
    }
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  const handleClick = (notif) => {
    if (!notif.is_read) markAsRead(notif.id);
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative h-9 w-9 text-slate-600 hover:text-[#155155] hover:bg-slate-100"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell-btn"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white animate-fade-in">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-lg shadow-xl border border-slate-200 z-50 animate-slide-in overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-heading font-semibold text-slate-900">
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] text-[#155155] hover:underline font-medium"
                  data-testid="mark-all-read-btn"
                >
                  Mark all read
                </button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-400 hover:text-slate-600"
                onClick={() => setOpen(false)}
                data-testid="close-notifications-btn"
              >
                <X size={14} />
              </Button>
            </div>
          </div>

          {/* Notification List */}
          <ScrollArea className="max-h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-5 w-5 border-2 border-[#155155]/20 border-t-[#155155] rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-10 px-4">
                <Bell size={28} className="text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No notifications yet</p>
                <p className="text-xs text-slate-300 mt-1">
                  You'll be notified about lead assignments, deal updates, and more
                </p>
              </div>
            ) : (
              <div>
                {notifications.map((notif) => (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-slate-50 transition-colors hover:bg-slate-50 ${
                      !notif.is_read ? "bg-blue-50/30" : ""
                    }`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={`p-1.5 rounded-lg mt-0.5 shrink-0 ${typeBg[notif.type] || "bg-slate-50"}`}>
                      {typeIcons[notif.type] || <Bell size={14} className="text-slate-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-[13px] leading-tight ${!notif.is_read ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                          {notif.title}
                        </p>
                        {!notif.is_read && (
                          <div className="h-1.5 w-1.5 rounded-full bg-[#155155] shrink-0" />
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">
                        {notif.message}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {timeAgo(notif.created_at)}
                      </p>
                    </div>
                    {!notif.is_read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notif.id);
                        }}
                        className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-[#155155] shrink-0 mt-0.5"
                        title="Mark as read"
                        data-testid={`mark-read-${notif.id}`}
                      >
                        <Check size={12} />
                      </button>
                    )}
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
